import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Star, Reply, ReplyAll, Forward, Trash2, Archive, MoreHorizontal,
  Paperclip, Download, ChevronDown, ChevronLeft, ChevronRight, Send,
  Loader2, Inbox, Eye, FolderInput, Pencil,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';
import { FOLDERS, getImapFolder } from '../../lib/folders';
import { getInitials, getAvatarColor } from '../../lib/avatar';
import { getFileMeta } from '../../lib/fileMeta';
import { htmlToPlainText } from '../../lib/compose';
import EmailBody from '../mail/EmailBody';
import ThreadMessages from '../mail/ThreadMessages';
import { labelsOfKeywords, withLabelChange } from '../../lib/labels';
import { LabelMenu, LabelChips } from '../labels/LabelMenu';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('tr-TR', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// base64 ek içeriğini Blob'a çevir
const blobFromAttachment = (attachment) => {
  const bytes = Uint8Array.from(atob(attachment.content), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: attachment.content_type || 'application/octet-stream' });
};

const toolbarBtn = 'h-9 gap-2 px-2.5 text-sm font-medium rounded-lg';

export const MailView = ({
  selectedMailId,
  currentFolder = 'inbox',
  onBack,
  isMobile = false,
  onCompose,
  user = null,
  onChanged,
  onEditDraft,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  labels = [],
  onManageLabels,
}) => {
  const [email, setEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [signature, setSignature] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [thread, setThread] = useState([]);

  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  const folderName = getImapFolder(currentFolder);

  const loadEmail = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getMessage(selectedMailId, folderName);
      setEmail(response);

      try {
        const { signatures } = await api.getSignatures();
        setSignature((signatures || []).find((sig) => sig.is_default_reply)?.html || '');
      } catch (err) {
        console.error('Failed to load signature:', err);
      }
    } catch (err) {
      console.error('Failed to load email:', err);
      setError(err.message || 'Mail yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  }, [selectedMailId, folderName]);

  useEffect(() => {
    setIsReplying(false);
    setReplyText('');
    setShowDetails(false);
    setEmail(null); // yükleme sırasında önceki mailin verisiyle işlem yapılmasın
    setThread([]);
    if (selectedMailId) loadEmail();
  }, [selectedMailId, loadEmail]);

  // Konuşmadaki diğer mailler (gelen + gönderilen + arşiv); başarısız olursa sessizce atlanır
  useEffect(() => {
    if (!selectedMailId || currentFolder === 'drafts') return undefined;
    let cancelled = false;
    api.getThread(selectedMailId, folderName)
      .then((items) => { if (!cancelled) setThread(items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedMailId, folderName, currentFolder]);

  // Önizleme blob URL'i: dialog kapanınca serbest bırak
  const previewUrl = useMemo(() => {
    if (!showPreviewDialog || !previewAttachment?.content) return null;
    try {
      return URL.createObjectURL(blobFromAttachment(previewAttachment));
    } catch (err) {
      console.error('Preview URL failed:', err);
      return null;
    }
  }, [showPreviewDialog, previewAttachment]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const downloadAttachment = (attachment, index) => {
    if (!attachment?.content) {
      toast.error('Ek içeriği bulunamadı');
      return;
    }
    try {
      const url = URL.createObjectURL(blobFromAttachment(attachment));
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename || `attachment_${index}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('İndirme başarısız');
    }
  };

  const downloadAll = () => {
    (email?.attachments || []).forEach((att, i) => setTimeout(() => downloadAttachment(att, i), i * 300));
  };

  const previewAttachmentFile = (attachment, index) => {
    if (!attachment.content) {
      toast.error('Ek içeriği bulunamadı');
      return;
    }
    const type = attachment.content_type || '';
    if (!type.startsWith('image/') && type !== 'application/pdf' && !type.startsWith('text/')) {
      downloadAttachment(attachment, index);
      return;
    }
    setPreviewAttachment(attachment);
    setShowPreviewDialog(true);
  };

  // ---- Mail işlemleri ----
  const runAction = async (action, successMessage, errorMessage, removed) => {
    setIsBusy(true);
    try {
      await action();
      toast.success(successMessage, { duration: 2500 });
      onChanged?.({ removed });
    } catch (err) {
      toast.error(errorMessage, { description: err.message || 'Bir hata oluştu' });
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = () =>
    runAction(() => api.deleteMessage(selectedMailId, folderName), 'Mail silindi', 'Mail silinemedi', true);

  const handleArchive = () =>
    runAction(
      () => api.moveMessage(selectedMailId, folderName, getImapFolder('archive')),
      'Mail arşivlendi', 'Arşivlenemedi', true,
    );

  const handleMove = (folder) =>
    runAction(
      () => api.moveMessage(selectedMailId, folderName, folder.imap),
      `"${folder.name}" klasörüne taşındı`, 'Taşınamadı', true,
    );

  const handleToggleLabel = async (label, currentlyOn) => {
    const add = currentlyOn ? [] : [label.id];
    const remove = currentlyOn ? [label.id] : [];
    const previous = email.keywords;
    setEmail((prev) => (prev ? { ...prev, keywords: withLabelChange(prev.keywords, add, remove) } : prev));
    try {
      await api.applyLabels(folderName, [selectedMailId], add, remove);
      onChanged?.({ removed: false });
    } catch (err) {
      setEmail((prev) => (prev ? { ...prev, keywords: previous } : prev));
      toast.error('Etiket uygulanamadı', { description: err.message || 'Bir hata oluştu' });
    }
  };

  const handleToggleStar = async () => {
    const next = !email.is_starred;
    setEmail({ ...email, is_starred: next });
    try {
      await api.toggleStar(selectedMailId, next, folderName);
      onChanged?.({ removed: false });
    } catch (err) {
      setEmail((prev) => (prev ? { ...prev, is_starred: !next } : prev));
      toast.error('İşlem başarısız', { description: err.message || 'Bir hata oluştu' });
    }
  };

  // ---- Yanıtla / Tümünü yanıtla / İlet ----
  const withPrefix = (prefix, subject) => {
    const s = subject || '';
    return s.toLowerCase().startsWith(prefix.toLowerCase()) ? s : `${prefix} ${s}`;
  };

  const quotedOriginal = () => {
    const orig = email.body_text && email.body_text.trim() ? email.body_text : htmlToPlainText(email.body_html);
    const dateStr = email.date ? new Date(email.date).toLocaleString('tr-TR') : '';
    const fromStr = email.from_addr ? `${email.from_addr.name || ''} <${email.from_addr.email}>`.trim() : '';
    return `\n\n----- Orijinal Mesaj -----\nKimden: ${fromStr}\nTarih: ${dateStr}\nKonu: ${email.subject || ''}\n\n${orig}`;
  };

  const selfEmail = (user?.email || '').toLowerCase();

  const handleReply = () => {
    if (!email || !onCompose) return;
    onCompose({
      mode: 'reply',
      to: [{ name: email.from_addr?.name || email.from_addr?.email, email: email.from_addr?.email }],
      subject: withPrefix('Re:', email.subject),
      body: quotedOriginal(),
    });
  };

  const handleReplyAll = () => {
    if (!email || !onCompose) return;
    const seen = new Set();
    const to = [];
    [email.from_addr, ...(email.to || [])].forEach((r) => {
      if (!r || !r.email) return;
      const e = r.email.toLowerCase();
      if (e === selfEmail || seen.has(e)) return;
      seen.add(e);
      to.push({ name: r.name || r.email, email: r.email });
    });
    const cc = (email.cc || [])
      .filter((r) => r && r.email && r.email.toLowerCase() !== selfEmail && !seen.has(r.email.toLowerCase()))
      .map((r) => ({ name: r.name || r.email, email: r.email }));
    onCompose({ mode: 'replyAll', to, cc, subject: withPrefix('Re:', email.subject), body: quotedOriginal() });
  };

  const handleForward = () => {
    if (!email || !onCompose) return;
    const attachments = (email.attachments || []).map((a) => ({
      name: a.filename,
      content: a.content,
      type: a.content_type || 'application/octet-stream',
    }));
    onCompose({ mode: 'forward', to: [], subject: withPrefix('Fwd:', email.subject), body: quotedOriginal(), attachments });
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setIsSendingReply(true);
    try {
      let bodyHtml = `<p>${replyText.replace(/\n/g, '<br>')}</p>`;
      let bodyText = replyText;
      if (signature) {
        bodyHtml += '<br><br>--<br>' + signature;
        bodyText += '\n\n--\n' + htmlToPlainText(signature);
      }
      await api.sendEmail([email.from_addr.email], `Re: ${email.subject}`, bodyHtml, bodyText);
      setReplyText('');
      setIsReplying(false);
      toast.success('Yanıt gönderildi', {
        description: `${email.from_addr.email} adresine yanıt gönderildi`,
        duration: 4000,
      });
    } catch (err) {
      toast.error('Yanıt gönderilemedi', { description: err.message || 'Bir hata oluştu', duration: 5000 });
    } finally {
      setIsSendingReply(false);
    }
  };

  // ---- Durumlar ----
  if (!selectedMailId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background" data-testid="no-mail-selected">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
            <Inbox className="h-10 w-10 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-1">Mail seçilmedi</h3>
          <p className="text-sm text-muted-foreground">Okumak için listeden bir mail seçin</p>
        </div>
      </div>
    );
  }

  // Yükleme/hata durumlarında araç çubuğu yerinde kalır; sadece içerik alanı değişir
  const loadingView = (
    <div className="flex-1 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );

  const errorView = (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
          <Inbox className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="font-medium mb-2">Yüklenemedi</h3>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={loadEmail} variant="outline" size="sm">Tekrar dene</Button>
      </div>
    </div>
  );

  const senderName = email?.from_addr?.name || email?.from_addr?.email || 'Bilinmeyen';
  const attachments = email?.attachments || [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex-1 min-w-0 flex flex-col bg-background overflow-hidden"
      data-testid="mail-view"
    >
      {/* Araç çubuğu */}
      <div className="h-14 shrink-0 flex items-center justify-between gap-2 px-3 sm:px-4 border-b border-border">
        <div className="flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-thin">
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={onBack} className="rounded-lg mr-1" aria-label="Geri" data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          {currentFolder === 'drafts' && (
            <Button className="h-9 gap-2 px-3 text-sm font-medium rounded-lg mr-1" onClick={() => onEditDraft?.(email)} disabled={!email} data-testid="edit-draft-button">
              <Pencil className="h-4 w-4" />
              Düzenle
            </Button>
          )}
          <Button variant="ghost" className={toolbarBtn} onClick={handleArchive} disabled={isBusy || currentFolder === 'archive'} title="Arşivle" data-testid="archive-button">
            <Archive className="h-4 w-4" />
            <span className="hidden xl:inline">Arşivle</span>
          </Button>
          <Button variant="ghost" className={`${toolbarBtn} hover:text-destructive`} onClick={handleDelete} disabled={isBusy} title="Sil" data-testid="delete-button">
            <Trash2 className="h-4 w-4" />
            <span className="hidden xl:inline">Sil</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className={toolbarBtn} disabled={isBusy} title="Taşı" data-testid="move-button">
                <FolderInput className="h-4 w-4" />
                <span className="hidden xl:inline">Taşı</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {FOLDERS.filter((f) => f.id !== currentFolder && !f.virtual).map((f) => (
                <DropdownMenuItem key={f.id} onClick={() => handleMove(f)}>{f.name}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <LabelMenu
            labels={labels}
            checked={(id) => labelsOfKeywords(email?.keywords, labels).some((l) => l.id === id)}
            onToggle={handleToggleLabel}
            onManage={onManageLabels}
            disabled={isBusy || !email}
            buttonClass={toolbarBtn}
          />

          <span className="h-5 w-px bg-border mx-1.5 shrink-0" />

          <Button variant="ghost" className={toolbarBtn} onClick={handleReply} disabled={!email} title="Yanıtla" data-testid="reply-button">
            <Reply className="h-4 w-4" />
            <span className="hidden 2xl:inline">Yanıtla</span>
          </Button>
          <Button variant="ghost" className={toolbarBtn} onClick={handleReplyAll} disabled={!email} title="Tümünü yanıtla" data-testid="reply-all-button">
            <ReplyAll className="h-4 w-4" />
            <span className="hidden 2xl:inline">Tümünü yanıtla</span>
          </Button>
          <Button variant="ghost" className={toolbarBtn} onClick={handleForward} disabled={!email} title="İlet" data-testid="forward-button">
            <Forward className="h-4 w-4" />
            <span className="hidden 2xl:inline">İlet</span>
          </Button>
        </div>

        {!isMobile && (
          <div className="flex items-center rounded-lg border border-border shrink-0">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-r-none" onClick={onPrev} disabled={!hasPrev} aria-label="Önceki mail" data-testid="prev-mail">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="h-5 w-px bg-border" />
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-l-none" onClick={onNext} disabled={!hasNext} aria-label="Sonraki mail" data-testid="next-mail">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* İçerik */}
      {isLoading ? loadingView : error ? errorView : !email ? null : (
      <ScrollArea className="flex-1">
        <div className="px-5 sm:px-8 py-6 max-w-4xl mx-auto">
          <h1 className="text-2xl font-semibold mb-2 break-words">{email.subject || '(Konu yok)'}</h1>
          <LabelChips labels={labelsOfKeywords(email.keywords, labels)} max={10} className="mb-4" />

          {/* Gönderen */}
          <div className="flex items-start gap-3 mb-5">
            <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold ${getAvatarColor(email.from_addr?.email || senderName)}`}>
              {getInitials(email.from_addr?.name, email.from_addr?.email)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-semibold">{senderName}</span>
                  {email.from_addr?.name && (
                    <span className="text-muted-foreground ml-2 text-sm break-all">&lt;{email.from_addr.email}&gt;</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm text-muted-foreground hidden sm:inline">{formatDate(email.date)}</span>
                  <button
                    onClick={handleToggleStar}
                    aria-label={email.is_starred ? 'Yıldızı kaldır' : 'Yıldızla'}
                    className="p-1 rounded hover:bg-secondary"
                    data-testid="star-button"
                  >
                    <Star className={`h-[18px] w-[18px] ${email.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`} />
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 max-w-full"
              >
                <span className="truncate">Kime: {email.to?.map((t) => t.name || t.email).join(', ')}</span>
                <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
              </button>

              {showDetails && (
                <div className="mt-2 p-3 bg-secondary rounded-lg text-sm space-y-1">
                  <p><span className="text-muted-foreground">Kimden:</span> {email.from_addr?.email}</p>
                  <p><span className="text-muted-foreground">Kime:</span> {email.to?.map((t) => t.email).join(', ')}</p>
                  {email.cc?.length > 0 && (
                    <p><span className="text-muted-foreground">Bilgi:</span> {email.cc.map((c) => c.email).join(', ')}</p>
                  )}
                  <p><span className="text-muted-foreground">Tarih:</span> {formatDate(email.date)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Mail gövdesi */}
          <div className="rounded-lg border border-border overflow-hidden bg-white mb-6">
            {email.body_html ? (
              <EmailBody key={email.id} html={email.body_html} />
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-sm p-4 text-gray-800">{email.body_text || 'İçerik yok'}</pre>
            )}
          </div>

          {/* Ekler */}
          {attachments.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  {attachments.length} ek
                </p>
                {attachments.length > 1 && (
                  <button onClick={downloadAll} className="text-sm text-primary hover:underline flex items-center gap-1.5" data-testid="download-all">
                    <Download className="h-4 w-4" />
                    Tümünü indir
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {attachments.map((attachment, index) => {
                  const { Icon, cls } = getFileMeta(attachment);
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-secondary/50 transition-colors"
                      data-testid={`attachment-${index}`}
                    >
                      <button
                        onClick={() => previewAttachmentFile(attachment, index)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        title="Önizle"
                      >
                        <span className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${cls}`}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium truncate">{attachment.filename}</span>
                          <span className="block text-xs text-muted-foreground">{attachment.size}</span>
                        </span>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Ek işlemleri">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => previewAttachmentFile(attachment, index)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Önizle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadAttachment(attachment, index)}>
                            <Download className="h-4 w-4 mr-2" />
                            İndir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ThreadMessages items={thread} currentId={selectedMailId} currentFolder={folderName} />

          {/* Hızlı yanıt */}
          <div className="rounded-xl border border-border overflow-hidden">
            {!isReplying ? (
              <button
                onClick={() => setIsReplying(true)}
                className="w-full flex items-center gap-2.5 px-4 py-3.5 text-sm text-muted-foreground hover:bg-secondary/60 transition-colors text-left"
                data-testid="quick-reply-button"
              >
                <Reply className="h-4 w-4 shrink-0" />
                <span className="truncate"><span className="font-medium text-foreground">{senderName}</span> kişisine yanıtla</span>
              </button>
            ) : (
              <div>
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-panel/60 text-sm text-muted-foreground">
                  <Reply className="h-4 w-4" />
                  <span className="truncate"><span className="font-medium text-foreground">{senderName}</span> kişisine yanıtla</span>
                </div>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Yanıtınızı yazın..."
                  autoFocus
                  className="min-h-[120px] resize-none border-0 rounded-none focus-visible:ring-0 px-4 py-3"
                  data-testid="reply-textarea"
                />
                {signature && (
                  <div className="mx-4 mb-3 px-4 py-3 border border-border rounded-lg bg-secondary/50">
                    <div className="text-xs text-muted-foreground mb-2">--</div>
                    <div className="rounded overflow-hidden"><EmailBody html={signature} padding={4} testId="reply-signature-frame" /></div>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
                  <Button variant="outline" onClick={() => { setIsReplying(false); setReplyText(''); }} disabled={isSendingReply}>
                    İptal
                  </Button>
                  <Button onClick={handleSendReply} disabled={!replyText.trim() || isSendingReply} data-testid="send-reply-button">
                    {isSendingReply ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gönderiliyor...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" />Gönder</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </ScrollArea>
      )}

      {/* Ek önizleme */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="attachment-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewAttachment && (() => { const { Icon } = getFileMeta(previewAttachment); return <Icon className="h-4 w-4" />; })()}
              {previewAttachment?.filename || 'Önizleme'}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            {previewAttachment && previewUrl && (
              <>
                {previewAttachment.content_type?.startsWith('image/') && (
                  <img src={previewUrl} alt={previewAttachment.filename} className="max-w-full mx-auto rounded-lg" />
                )}
                {previewAttachment.content_type === 'application/pdf' && (
                  <iframe src={previewUrl} className="w-full h-[65vh] rounded-lg border" title={previewAttachment.filename} />
                )}
                {previewAttachment.content_type?.startsWith('text/') && (
                  <pre className="p-4 bg-muted rounded-lg overflow-auto text-sm whitespace-pre-wrap">
                    {(() => {
                      try { return atob(previewAttachment.content); } catch { return 'İçerik okunamadı'; }
                    })()}
                  </pre>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Kapat</Button>
            <Button onClick={() => { downloadAttachment(previewAttachment, 0); setShowPreviewDialog(false); }}>
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default MailView;
